import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";

import {
  getAllDisciplines,
  addDiscipline,
  updateDiscipline,
  deleteDiscipline,
  updateOrderDiscipline,
} from "../../../../../../../services/disciplinesServices";

import { uploadDisciplinePhoto } from "../../../../../../../services/disciplinePhotoServices";

import {
  buildDisciplinePayload,
  calculateDisciplinesStats,
  extractCreatedDisciplineId,
  filterDisciplines,
  getNextDisciplineOrder,
  normalizeOrderDisciplines,
  sortDisciplinesByOrder,
  validateDisciplinePayload,
  validateImageFile,
} from "../utils/disciplinesSettingsUtils";

const extractDisciplineImageUrl = (response) => {
  if (!response) return "";

  return (
    response?.image_discipline ||
    response?.photo_url ||
    response?.imageUrl ||
    response?.url ||
    response?.path ||
    response?.data?.image_discipline ||
    response?.data?.photo_url ||
    response?.data?.imageUrl ||
    response?.data?.url ||
    response?.data?.path ||
    ""
  );
};

export function useDisciplinesSettings() {
  const [disciplines, setDisciplines] = useState([]);
  const [loading, setLoading] = useState(true);

  const [filters, setFilters] = useState({
    query: "",
  });

  const [formDrawerOpen, setFormDrawerOpen] = useState(false);
  const [formMode, setFormMode] = useState("create");
  const [editableDiscipline, setEditableDiscipline] = useState(null);
  const [submittingForm, setSubmittingForm] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);

  const [deletingId, setDeletingId] = useState(null);
  const [reordering, setReordering] = useState(false);

  const fetchDisciplines = useCallback(async () => {
    try {
      setLoading(true);

      const data = await getAllDisciplines();
      setDisciplines(sortDisciplinesByOrder(Array.isArray(data) ? data : []));
    } catch (error) {
      console.error("Erreur chargement disciplines :", error);
      toast.error("Impossible de charger les disciplines.");
      setDisciplines([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchDisciplines();
  }, [fetchDisciplines]);

  const filteredDisciplines = useMemo(() => {
    return filterDisciplines(disciplines, filters);
  }, [disciplines, filters]);

  const stats = useMemo(
    () => calculateDisciplinesStats(disciplines),
    [disciplines]
  );

  const filteredStats = useMemo(
    () => calculateDisciplinesStats(filteredDisciplines),
    [filteredDisciplines]
  );

  const nextOrder = useMemo(
    () => getNextDisciplineOrder(disciplines),
    [disciplines]
  );

  const setFilter = (key, value) => {
    setFilters((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  const resetFilters = () => {
    setFilters({
      query: "",
    });
  };

  const updateLocalDisciplineImage = useCallback((disciplineId, imageUrl) => {
    if (!disciplineId || !imageUrl) return;

    setDisciplines((prev) =>
      sortDisciplinesByOrder(
        prev.map((discipline) =>
          String(discipline.id) === String(disciplineId)
            ? {
                ...discipline,
                image_discipline: imageUrl,
              }
            : discipline
        )
      )
    );

    setEditableDiscipline((prev) =>
      prev && String(prev.id) === String(disciplineId)
        ? {
            ...prev,
            image_discipline: imageUrl,
          }
        : prev
    );
  }, []);

  const openCreateDrawer = () => {
    setFormMode("create");
    setEditableDiscipline(null);
    setUploadProgress(0);
    setFormDrawerOpen(true);
  };

  const openEditDrawer = (discipline) => {
    setFormMode("edit");
    setEditableDiscipline(discipline || null);
    setUploadProgress(0);
    setFormDrawerOpen(true);
  };

  const closeFormDrawer = () => {
    if (submittingForm) return;

    setFormDrawerOpen(false);
    setEditableDiscipline(null);
    setFormMode("create");
    setUploadProgress(0);
  };

  const closeAfterSubmit = () => {
    setFormDrawerOpen(false);
    setEditableDiscipline(null);
    setFormMode("create");
    setUploadProgress(0);
  };

  const handleSubmitDiscipline = async ({
    formData,
    file,
    outputFormat = "webp",
  }) => {
    const payload = buildDisciplinePayload(formData);
    const payloadError = validateDisciplinePayload(payload);
    const fileError = validateImageFile(file);

    if (payloadError) {
      toast.error(payloadError);
      return;
    }

    if (fileError) {
      toast.error(fileError);
      return;
    }

    try {
      setSubmittingForm(true);
      setUploadProgress(0);

      if (formMode === "create") {
        const created = await addDiscipline(payload);
        const createdId = extractCreatedDisciplineId(created);

        if (!created) {
          toast.error("La discipline n’a pas pu être créée.");
          return;
        }

        if (file && createdId) {
          const uploaded = await uploadDisciplinePhoto(createdId, file, {
            format: outputFormat,
            onUploadProgress: (event) => {
              if (!event.total) return;

              setUploadProgress(
                Math.round((event.loaded * 100) / event.total)
              );
            },
          });

          const uploadedImage = extractDisciplineImageUrl(uploaded);

          if (uploadedImage) {
            updateLocalDisciplineImage(createdId, uploadedImage);
          } else {
            toast.warning(
              "Image envoyée, mais l’API n’a pas renvoyé d’URL exploitable."
            );
          }
        }

        if (file && !createdId) {
          toast.warning(
            "La discipline a été créée, mais l’identifiant retourné par l’API ne permet pas d’envoyer l’image automatiquement."
          );
        }

        toast.success("Discipline créée avec succès.");
      } else {
        if (!editableDiscipline?.id) {
          toast.error("Discipline introuvable.");
          return;
        }

        await updateDiscipline(editableDiscipline.id, payload);

        if (file) {
          const uploaded = await uploadDisciplinePhoto(editableDiscipline.id, file, {
            format: outputFormat,
            onUploadProgress: (event) => {
              if (!event.total) return;

              setUploadProgress(
                Math.round((event.loaded * 100) / event.total)
              );
            },
          });

          const uploadedImage = extractDisciplineImageUrl(uploaded);

          if (uploadedImage) {
            updateLocalDisciplineImage(editableDiscipline.id, uploadedImage);
          } else {
            toast.warning(
              "Image envoyée, mais l’API n’a pas renvoyé d’URL exploitable."
            );
          }
        }

        toast.success("Discipline mise à jour avec succès.");
      }

      await fetchDisciplines();
      closeAfterSubmit();
    } catch (error) {
      console.error("Erreur enregistrement discipline :", error);

      toast.error(
        error?.response?.data?.message ||
          error?.response?.data ||
          error?.message ||
          "Erreur lors de l’enregistrement de la discipline."
      );
    } finally {
      setSubmittingForm(false);
      setUploadProgress(0);
    }
  };

  const handleDeleteDiscipline = async (discipline) => {
    if (!discipline?.id) return;

    if (
      !window.confirm(
        `Êtes-vous sûr de vouloir supprimer « ${
          discipline.nom_discipline || "discipline sans nom"
        } » ?`
      )
    ) {
      return;
    }

    try {
      setDeletingId(discipline.id);

      await deleteDiscipline(discipline.id);

      toast.success("Discipline supprimée avec succès.");
      await fetchDisciplines();
    } catch (error) {
      console.error("Erreur suppression discipline :", error);
      toast.error("Erreur lors de la suppression de la discipline.");
    } finally {
      setDeletingId(null);
    }
  };

  const persistOrder = async (orderedRows) => {
    await updateOrderDiscipline(orderedRows);
  };

  const applyReorder = async (rows) => {
    const orderedRows = normalizeOrderDisciplines(rows);

    try {
      setReordering(true);
      setDisciplines(orderedRows);

      await persistOrder(orderedRows);
      toast.success("Ordre des disciplines mis à jour.");
      await fetchDisciplines();
    } catch (error) {
      console.error("Erreur ordre disciplines :", error);
      toast.error("Erreur lors de la mise à jour de l’ordre.");
      await fetchDisciplines();
    } finally {
      setReordering(false);
    }
  };

  const moveDiscipline = async (disciplineId, direction) => {
    if (reordering || loading) return;

    const rows = sortDisciplinesByOrder(disciplines);
    const index = rows.findIndex((discipline) => discipline.id === disciplineId);

    if (index === -1) return;

    const targetIndex = direction === "up" ? index - 1 : index + 1;

    if (targetIndex < 0 || targetIndex >= rows.length) return;

    const nextRows = [...rows];
    const current = nextRows[index];

    nextRows[index] = nextRows[targetIndex];
    nextRows[targetIndex] = current;

    await applyReorder(nextRows);
  };

  return {
    disciplines,
    filteredDisciplines,
    loading,

    filters,
    setFilter,
    resetFilters,

    stats,
    filteredStats,
    nextOrder,

    formDrawerOpen,
    formMode,
    editableDiscipline,
    submittingForm,
    uploadProgress,
    openCreateDrawer,
    openEditDrawer,
    closeFormDrawer,
    handleSubmitDiscipline,

    deletingId,
    reordering,
    moveDiscipline,

    fetchDisciplines,
    handleDeleteDiscipline,
  };
}